import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ElementRef, Injectable, Renderer2, inject, DOCUMENT } from '@angular/core';
import { NavigationStart, Route, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { ApWebcomponentConfig, Script, WebcomponentEvent } from './webcomponent.model';

@Injectable({
    providedIn: 'root'
})
export class ApWebcomponentService {
    protected readonly document = inject<Document>(DOCUMENT);
    protected readonly httpClient = inject(HttpClient);
    protected readonly location = inject(Location);

    private injectedScripts: string[];
    private injectedStylesheets: string[];

    public constructor() {
        this.injectedScripts = [];
        this.injectedStylesheets = [];
    }

    public getConfiguration(deployUrl: string): Observable<ApWebcomponentConfig> {
        return this.httpClient.get<ApWebcomponentConfig>(`${deployUrl}/webcomponent.json`);
    }

    public addScriptTag(script: Script, deployUrl: string, elementRef: ElementRef, renderer: Renderer2): Observable<string> {
        const scriptPath = `${deployUrl}/${script.name}`;
        if (!this.isScriptInjected(scriptPath)) {
            // see documentation for script tag https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script
            const scriptTag: HTMLScriptElement = this.document.createElement('script');
            const scriptElement: HTMLScriptElement = this.document.querySelector('body > script');
            const scriptNonce = (scriptElement || {}).nonce;
            scriptTag.src = scriptPath;
            scriptTag.setAttribute('nonce', scriptNonce);
            if (!script.module) {
                scriptTag.noModule = true;
            }
            renderer.appendChild(elementRef.nativeElement, scriptTag);
            this.injectedScripts.push(scriptPath);
        }
        return of('');
    }

    public addStyleSheet(href: string, elementRef: ElementRef, renderer: Renderer2): Observable<string> {
        if (!this.isStylesheetInjected(href)) {
            const styleTag: HTMLLinkElement = this.document.createElement('link');
            styleTag.rel = 'stylesheet';
            styleTag.href = href;
            renderer.appendChild(elementRef.nativeElement, styleTag);
            this.injectedStylesheets.push(href);
        }
        return of('');
    }

    public addWebcomponentTag(tagName: string, elementRef: ElementRef, renderer: Renderer2): void {
        this.removeWebcomponentTag(tagName, elementRef, renderer);
        const webcomponentTag: HTMLElement = this.document.createElement(tagName);
        renderer.appendChild(elementRef.nativeElement, webcomponentTag);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public addWebcomponentEventListener(tagName: string, customEvent: WebcomponentEvent): void {
        throw new Error('override addWebcomponentEventListener');
    }

    public clear(): void {
        this.injectedScripts = [];
        this.injectedStylesheets = [];
    }

    public listenToWebcomponentRouteChangeEvent(tagName: string, routes: Route[]): void {
        const element = this.document.querySelector(tagName);
        element.addEventListener('WebcomponentRouteChanged', (event: any) => {
            const hasRoute = this.hasMatchingRoute(`/${event.detail}`, routes);

            if (hasRoute) {
                this.location.replaceState(`/${event.detail}`);
            }
        });
    }

    public webcomponentRouteChangedEvent(router: Router, wcPath: string): Observable<CustomEvent> {
        const baseUrl = this.getWebcomponentBasePath(wcPath);
        return router.events.pipe(
            filter((event) => event instanceof NavigationStart),
            switchMap((navigationStart: NavigationStart) => {
                const url = navigationStart.url === '/' ? baseUrl : baseUrl + navigationStart.url;
                const event: CustomEvent = new CustomEvent('WebcomponentRouteChanged', {
                    bubbles: true,
                    detail: url
                });
                return of(event);
            })
        );
    }

    protected removeWebcomponentTag(tagName: string, elementRef: ElementRef, renderer: Renderer2): void {
        Array.from(elementRef.nativeElement.children).forEach((child: any) => {
            if (tagName.toUpperCase() === child.tagName) {
                renderer.removeChild(elementRef.nativeElement, child);
            }
        });
    }

    private isScriptInjected(src: string): boolean {
        return this.injectedScripts.find((script: string) => script === src) !== undefined;
    }

    private isStylesheetInjected(href: string): boolean {
        return this.injectedStylesheets.find((stylesheet: string) => stylesheet === href) !== undefined;
    }

    private getWebcomponentBasePath(webcomponentPath: string): string {
        if (this.document.location.hash) {
            const fullBaseUrl =
                webcomponentPath === ''
                    ? this.document.location.hash
                    : this.document.location.hash.substring(0, this.document.location.hash.indexOf(`${webcomponentPath}`));
            return fullBaseUrl.substring(2); // remove hash
        }
        const href = this.document.location.href;
        const baseUri = this.document.baseURI;
        return webcomponentPath === ''
            ? href.substring(href.indexOf(baseUri) + baseUri.length)
            : href.substring(href.indexOf(baseUri) + baseUri.length, href.indexOf(`${webcomponentPath}`));
    }

    private hasMatchingRoute(routePathToCheck: string, routes: Route[], routePath = ''): boolean {
        for (const route of routes) {
            const routePathParts = route.path
                // double wildcard replacement: `path/*/sub/**` becomes `path/*/sub/.*`
                .replace(/\*\*/, '.*')
                // single wildcard replacement: `path/*/sub/.*` becomes `path/[^/]+/sub/.*`
                .replace(/(?<!\.)\*/g, '[^/]+')
                .split('/')
                .map((pathPart: string) => (pathPart.startsWith(':') ? '[^,;\\/]+' : pathPart));
            const path = routePathParts[0] === '' && routePathParts.length === 1 ? routePath : [routePath, ...routePathParts].join('/');
            const regexp = `^${path}$`;

            if (
                (route.children && this.hasMatchingRoute(routePathToCheck, route.children, path)) ||
                routePathToCheck.match(regexp) !== null
            ) {
                return true;
            }
        }
        return false;
    }
}
